import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Info } from 'lucide-react';
import { useHFBProjectAllocationsForDonut, useUserProjectHistory } from '../../hooks/useHFBData';

// Site Visit constants (module-level so they're referentially stable)
const SITE_VISIT_TASK_ID = 'site';           // Virtual task ID for UI (never sent to DB)
const SITE_VISIT_CC_TASK_ID = 16;            // Maps to CC stage in DB
const SITE_VISIT_DESCRIPTION = 'Site Visit'; // Marker in description field

// Internal office projects (BRIO_General, BRIO_Administration) have no site and no
// construction phase, so the wheel offers them neither CC nor the virtual SITE task.
const NO_CC_SITE_PROJECT_CODES = new Set(['BRG', 'BRA']);

// Time-logging window (in days). Employees can log time on any day within this
// look-back window via the day strip. It must be wide enough that when an admin
// retroactively re-opens a past week on the Focus Board, the affected employees
// can still reach those days here to log the previously-missed hours. Keep this
// aligned with (or wider than) the oldest week admins realistically re-open.
const PENDING_DAYS_LOOKBACK = 35;   // how far back un-logged days stay loggable
const COMPLETED_DAYS_LOOKBACK = 35; // how far back logged days are scanned for the review strip
const COMPLETED_DAYS_SHOWN = 5;     // max completed day cards shown in the review strip

/**
 * DonutTimeTracker - Minimalist BRIO Edition
 * 
 * Integrated version that works with the main app's data sources:
 * - Receives data via props (activeTimeEntries, activeProjects, etc.)
 * - Uses CRUD callbacks for Supabase/local mode compatibility
 * - No localStorage reads/writes inside this component
 */
export default function DonutTimeTracker({
  // Data mode
  useSupabaseDB = false,
  
  // NEW: Enable fetching allocations from Supabase (weekly_project_allocations table)
  useSupabaseAllocations = false,
  
  // Current user
  currentUser,
  
  // Active data sources (Supabase-aware)
  activeTimeEntries = [],
  activeProjects = [
    { id: 1, name: 'Website Redesign', projectCode: 'WEB', color: '#3B82F6' },
    { id: 2, name: 'Mobile App', projectCode: 'MOB', color: '#10B981' },
    { id: 3, name: 'API Development', projectCode: 'API', color: '#F59E0B' }
  ],
  activeUsers = [],
  activeTasks = [
    { id: 1, name: 'Design' },
    { id: 2, name: 'Development' },
    { id: 3, name: 'Testing' }
  ],
  allocations = [
    { id: 1, userId: 1, projectId: 1, weekStart: new Date(Date.now() - new Date().getDay() * 86400000).toISOString().split('T')[0], allocatedHours: 20 },
    { id: 2, userId: 1, projectId: 2, weekStart: new Date(Date.now() - new Date().getDay() * 86400000).toISOString().split('T')[0], allocatedHours: 15 },
    { id: 3, userId: 1, projectId: 3, weekStart: new Date(Date.now() - new Date().getDay() * 86400000).toISOString().split('T')[0], allocatedHours: 10 }
  ],
  activeAllocations = null, // New: Supabase-aware allocations (falls back to allocations if null)

  // Holidays array for date dot indicators
  holidays = [],

  // Invoices array — used to filter out fully-invoiced stages from wheel picker
  invoices = [],

  // Billable default helper — returns true if project has external client
  isProjectBillable = () => true,

  // CRUD callbacks for time entries
  onCreateTimeEntry,
  onUpdateTimeEntry,
  onDeleteTimeEntry,
  onRefetch, // Called once after batch operations (e.g., confirm day)

  // Site visit: configurable backend hours (default 10)
  siteVisitBackendHours = 10,

  // Wheel picker scroll speed (0.25–1.0, default 0.5)
  wheelPickerSpeed = 0.5,

  // Nav height from App.js (accounts for main header + submenu)
  navHeight = 120,

  // Local state setters (for local mode)
  setTimeEntries,
  timeEntries = []
}) {
  // ============================================================================
  // STATE
  // ============================================================================
  const [confirmedDays, setConfirmedDays] = useState([]);
  const [activeDate, setActiveDate] = useState(null);
  const [hoveredSegment, setHoveredSegment] = useState(null);
  const [hoveredDay, setHoveredDay] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartAngle, setDragStartAngle] = useState(null);
  const [dragSegmentIndex, setDragSegmentIndex] = useState(null);
  const [dragStartHours, setDragStartHours] = useState(null);
  const [isConfirming, setIsConfirming] = useState(false);
  
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [currentEntries, setCurrentEntries] = useState([]);
  const [_lastClickTime, _setLastClickTime] = useState(0); // eslint-disable-line no-unused-vars
  const [_lastClickSegment, _setLastClickSegment] = useState(null); // eslint-disable-line no-unused-vars
  
  const containerRef = useRef(null);
  const svgRef = useRef(null);


  const projectWheelRef = useRef(null);
  const taskWheelRef = useRef(null);
  
  // Debounce timers for wheel pickers (fallback when scrollend isn't supported)
  const projectScrollTimerRef = useRef(null);
  const taskScrollTimerRef = useRef(null);

  // ── Wheel picker infrastructure ──
  // Constant item height used everywhere (CSS + JS). Update this ONE value if layout changes.
  const WHEEL_ITEM_HEIGHT = 40;

  // Click-guard: when true, the scroll-settle handler ignores events (prevents click → scroll → overwrite)
  const projectClickGuardRef = useRef(false);
  const taskClickGuardRef = useRef(false);

  // Confirm-guard: prevents double-click from submitting entries twice
  const confirmGuardRef = useRef(false);

  // Ref for tracking double-click on saved segments (for delete)
  const lastSavedClickRef = useRef({ entryId: null, time: 0 });
  
  // Hard guard: IDs that are currently being deleted - NEVER removed until entry disappears or long timeout
  const deletingSavedIdsRef = useRef(new Set());
  
  // Track saved segment being dragged for persisting on release
  const draggingSavedSegmentRef = useRef(null);

  // Accumulated angle delta across all pointermove frames (avoids 180° snap-back bug)
  const dragPrevAngleRef = useRef(null);
  const dragAccumulatedDeltaRef = useRef(0);

  // Cleanup scroll debounce timers on unmount
  useEffect(() => {
    return () => {
      clearTimeout(projectScrollTimerRef.current);
      clearTimeout(taskScrollTimerRef.current);
    };
  }, []);

  // ── Wheel settle helper ──
  // Computes the nearest item index from scrollTop, snaps to it, and calls the setter.
  // Shared by both project and task wheels. Does NOT reference picker lists directly —
  // the lists are passed in by the useEffect that calls this.
  const settleWheel = useCallback((scrollEl, items, setSelected, clickGuardRef) => {
    if (!scrollEl || items.length === 0) return;
    // If a click just programmatically scrolled, skip — the click handler already set selection
    if (clickGuardRef.current) return;

    const scrollTop = scrollEl.scrollTop;
    const rawIndex = scrollTop / WHEEL_ITEM_HEIGHT;
    const index = Math.max(0, Math.min(Math.round(rawIndex), items.length - 1));

    // Snap to computed position (corrects any sub-pixel drift)
    const targetTop = index * WHEEL_ITEM_HEIGHT;
    if (Math.abs(scrollTop - targetTop) > 1) {
      scrollEl.scrollTo({ top: targetTop, behavior: 'smooth' });
    }

    setSelected(items[index].id);
  }, []);

  // ── Click-to-select helper ──
  // Sets selection, scrolls to item, and temporarily blocks the scroll-settle handler.
  const clickWheelItem = useCallback((wheelRef, index, id, setSelected, clickGuardRef) => {
    clickGuardRef.current = true;
    setSelected(id);
    if (wheelRef.current) {
      wheelRef.current.scrollTo({ top: index * WHEEL_ITEM_HEIGHT, behavior: 'smooth' });
    }
    // Release guard after scroll animation completes
    setTimeout(() => { clickGuardRef.current = false; }, 320);
  }, []);

  // ── Discrete wheel stepper ──
  // Controls all wheel-driven scrolling. Native scroll is fully blocked via a non-passive
  // wheel listener (attached in useEffect below, NOT via React onWheel which is passive).
  // PC mouse notched wheels fire a burst of large-delta events over 500ms+ per flick;
  // trackpads fire many small-delta events. Each path gets its own step logic.
  const wheelCooldownRef = useRef(false);
  const wheelDeltaRef = useRef(0);
  const wheelDeltaResetRef = useRef(null);
  const wheelTargetElRef = useRef(null);    // remember which wheel element is being scrolled
  const wheelCooldownTimerRef = useRef(null);
  // Scale thresholds/cooldowns by wheelPickerSpeed (0.25–1.0). Higher speed = lower cooldowns.
  const speedFactor = Math.max(0.25, Math.min(1, wheelPickerSpeed));
  const WHEEL_DELTA_THRESHOLD_TRACKPAD = Math.round(30 + 30 * (1 - speedFactor)); // 30–60px
  const WHEEL_COOLDOWN_TRACKPAD = Math.round(80 + 120 * (1 - speedFactor));       // 80–200ms
  const WHEEL_COOLDOWN_PC_MOUSE = Math.round(180 + 220 * (1 - speedFactor));      // 180–400ms

  // Step the wheel by one item in direction, then start cooldown.
  // When cooldown expires, check if more delta accumulated and step again.
  const stepWheel = useCallback((el, direction, cooldownMs) => {
    const currentIndex = Math.round(el.scrollTop / WHEEL_ITEM_HEIGHT);
    const nextIndex = currentIndex + direction;
    el.scrollTo({ top: nextIndex * WHEEL_ITEM_HEIGHT, behavior: 'smooth' });

    wheelCooldownRef.current = true;
    clearTimeout(wheelCooldownTimerRef.current);
    wheelCooldownTimerRef.current = setTimeout(() => {
      wheelCooldownRef.current = false;
      // Check if deltas accumulated during cooldown — if so, step again
      if (Math.abs(wheelDeltaRef.current) >= WHEEL_DELTA_THRESHOLD_TRACKPAD && wheelTargetElRef.current) {
        const nextDir = wheelDeltaRef.current > 0 ? 1 : -1;
        wheelDeltaRef.current = 0;
        stepWheel(wheelTargetElRef.current, nextDir, cooldownMs);
      }
    }, cooldownMs);
  }, []);

  const dampenWheelScroll = useCallback((e) => {
    e.preventDefault(); // works because listener is { passive: false }
    e.stopPropagation();

    // Normalize delta: PC mice produce ~100-120px per notch, trackpads ~1-10px.
    let rawDelta = e.deltaY;
    if (e.deltaMode === 1) rawDelta *= 20;       // line mode → pixels
    else if (e.deltaMode === 2) rawDelta *= 400; // page mode → pixels

    const absRaw = Math.abs(rawDelta);
    const isPcMouse = absRaw > 50;
    const el = e.currentTarget;
    wheelTargetElRef.current = el;

    // Always accumulate delta, even during cooldown
    wheelDeltaRef.current += rawDelta;
    clearTimeout(wheelDeltaResetRef.current);
    wheelDeltaResetRef.current = setTimeout(() => { wheelDeltaRef.current = 0; }, 300);

    // During cooldown, just accumulate — the cooldown expiry handler will pick it up
    if (wheelCooldownRef.current) return;

    if (isPcMouse) {
      // PC mouse notch: step exactly one item, then lock out remaining burst events.
      const direction = rawDelta > 0 ? 1 : -1;
      wheelDeltaRef.current = 0;
      clearTimeout(wheelDeltaResetRef.current);
      stepWheel(el, direction, WHEEL_COOLDOWN_PC_MOUSE);
      return;
    }

    // Trackpad: accumulate small deltas until threshold, then step once
    if (Math.abs(wheelDeltaRef.current) < WHEEL_DELTA_THRESHOLD_TRACKPAD) return;

    const direction = wheelDeltaRef.current > 0 ? 1 : -1;
    wheelDeltaRef.current = 0;
    stepWheel(el, direction, WHEEL_COOLDOWN_TRACKPAD);
  }, [stepWheel]);

  // Attach non-passive wheel listeners so preventDefault() actually blocks native scroll.
  // React's onWheel is passive by default and cannot prevent scrolling.
  useEffect(() => {
    const opts = { passive: false };
    const pEl = projectWheelRef.current;
    const tEl = taskWheelRef.current;
    if (pEl) pEl.addEventListener('wheel', dampenWheelScroll, opts);
    if (tEl) tEl.addEventListener('wheel', dampenWheelScroll, opts);
    return () => {
      if (pEl) pEl.removeEventListener('wheel', dampenWheelScroll, opts);
      if (tEl) tEl.removeEventListener('wheel', dampenWheelScroll, opts);
    };
  }, [dampenWheelScroll]);

  // ============================================================================
  // FULLSCREEN ON MOUNT
  // Automatically enters browser fullscreen when this page is rendered.
  // No visible toggle button — fullscreen is page-driven only.
  // External daily launcher scripts (10am cron) are unaffected.
  // ============================================================================
  const [showFullscreenHint, setShowFullscreenHint] = useState(false);
  
  useEffect(() => {
    const canFullscreen = typeof document !== 'undefined' && !!document.documentElement?.requestFullscreen;
    if (!canFullscreen) return;
    
    // If already in fullscreen (e.g. external script opened us), do nothing
    if (document.fullscreenElement) return;

    let gestureCleanup = null;

    const tryEnter = () =>
      document.documentElement.requestFullscreen().then(() => {
        setShowFullscreenHint(false);
      }).catch(() => {});

    // Attempt auto-fullscreen; fall back to first user gesture if browser blocks it
    tryEnter().catch(() => {
      setShowFullscreenHint(true);
      const onGesture = () => {
        tryEnter();
        document.removeEventListener('click', onGesture, true);
        document.removeEventListener('keydown', onGesture, true);
      };
      document.addEventListener('click', onGesture, true);
      document.addEventListener('keydown', onGesture, true);
      gestureCleanup = () => {
        document.removeEventListener('click', onGesture, true);
        document.removeEventListener('keydown', onGesture, true);
      };
    });

    // Exit fullscreen when navigating away from this page
    return () => {
      if (gestureCleanup) gestureCleanup();
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(() => {});
      }
    };
  }, []);

  // ============================================================================
  // SUPABASE ALLOCATIONS HOOK
  // ============================================================================
  // Compute the Monday of the week for the active date (for Supabase query)
  const weekStartForSupabase = useMemo(() => {
    if (!activeDate) return null;
    // Parse the date string as local date
    const d = new Date(activeDate + 'T00:00:00');
    const day = d.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    // Calculate days since Monday (Monday = 0, Sunday = 6)
    const daysSinceMonday = day === 0 ? 6 : day - 1;
    // Subtract to get to Monday
    d.setDate(d.getDate() - daysSinceMonday);
    // Format as YYYY-MM-DD using local date parts
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const dayOfMonth = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${dayOfMonth}`;
  }, [activeDate]);

  // Fetch allocations from Supabase when useSupabaseAllocations is enabled
  const supabaseAllocationsHook = useHFBProjectAllocationsForDonut(
    useSupabaseAllocations ? weekStartForSupabase : null
  );
  const supabaseAllocations = supabaseAllocationsHook.allocations;

  // Fetch all project IDs the user has ever logged time on.
  // Runs once on mount — keeps previously-worked-on projects visible in the picker
  // even when weekly allocations change between weeks.
  const { projectIds: historicalProjectIds } = useUserProjectHistory(
    useSupabaseAllocations ? currentUser?.id : null
  );

  // ============================================================================
  // RESOLVED ALLOCATIONS (Supabase-aware)
  // ============================================================================
  // Priority: Supabase allocations (if enabled) > activeAllocations > allocations
  // FIX: When useSupabaseAllocations is true, ALWAYS use Supabase result (even if empty)
  // This ensures HFB edits are reflected correctly and prevents fallback to stale prop data
  const resolvedAllocations = useMemo(() => {
    // If Supabase allocations are enabled, use them unconditionally (even if empty array)
    if (useSupabaseAllocations && supabaseAllocations) {
      return supabaseAllocations; // Could be empty array - that's intentional
    }
    // Fallback to prop-based allocations only when NOT in Supabase mode
    return activeAllocations ?? allocations;
  }, [useSupabaseAllocations, supabaseAllocations, activeAllocations, allocations]);

  // ============================================================================
  // FLOATING USER DETECTION
  // ============================================================================
  // A "floating" or "unassigned" user is one who is NOT assigned to any team.
  // Check both camelCase (teamId) and snake_case (team_id) for Supabase compatibility.
  const isFloatingUser = useMemo(() => {
    if (!currentUser) return false;
    const teamId = currentUser.teamId ?? currentUser.team_id;
    // Treat null, undefined, empty string, or '0' as "no team"
    return !teamId || teamId === '' || teamId === 0 || teamId === '0';
  }, [currentUser]);

  // ============================================================================
  // UTILITY FUNCTIONS
  // ============================================================================
  
  /**
   * Get the Monday-based week start for a given date string.
   * Returns YYYY-MM-DD format using local time (no UTC conversion).
   * This matches the Supabase tt_allocations.week_start column format.
   */
  const getWeekStartLocalMonday = (dateStr) => {
    // Parse the date string as local date
    const d = new Date(dateStr + 'T00:00:00');
    const day = d.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    // Calculate days since Monday (Monday = 0, Sunday = 6)
    const daysSinceMonday = day === 0 ? 6 : day - 1;
    // Subtract to get to Monday
    d.setDate(d.getDate() - daysSinceMonday);
    // Format as YYYY-MM-DD using local date parts
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const dayOfMonth = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${dayOfMonth}`;
  };

  // Legacy getWeekStart kept for backwards compatibility (Sunday-based)
  const _getWeekStart = (date) => { // eslint-disable-line no-unused-vars
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day;
    return new Date(d.setDate(diff)).toISOString().split('T')[0];
  };

  const isWeekend = (date) => {
    const d = new Date(date);
    return d.getDay() === 0 || d.getDay() === 6;
  };

  const isHoliday = useCallback((dateStr) => {
    if (!holidays || holidays.length === 0) return false;
    return holidays.some(h => h.date === dateStr);
  }, [holidays]);

  // Wrapped in useCallback to satisfy ESLint exhaustive-deps
  const getPendingDays = useCallback(() => {
    const today = new Date();
    const days = [];

    for (let i = 1; i <= PENDING_DAYS_LOOKBACK; i++) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      
      const hasEntries = activeTimeEntries.some(e => e.date === dateStr && String(e.userId) === String(currentUser?.id));
      const isConfirmed = confirmedDays.includes(dateStr);
      
      if (!hasEntries && !isConfirmed) {
        days.push({
          date: dateStr,
          dateObj: date,
          isWeekend: isWeekend(dateStr),
          isHoliday: isHoliday(dateStr),
          isPending: true
        });
      }
    }

    return days.reverse();
  }, [activeTimeEntries, currentUser?.id, confirmedDays, isHoliday]);

  const getCompletedDays = useCallback(() => {
    const days = [];
    const today = new Date();

    for (let i = 1; i <= COMPLETED_DAYS_LOOKBACK && days.length < COMPLETED_DAYS_SHOWN; i++) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      
      const hasEntries = activeTimeEntries.some(e => e.date === dateStr && String(e.userId) === String(currentUser?.id));
      const isConfirmed = confirmedDays.includes(dateStr);
      
      if (hasEntries || isConfirmed) {
        days.push({
          date: dateStr,
          dateObj: date,
          isWeekend: isWeekend(dateStr),
          isHoliday: isHoliday(dateStr),
          isCompleted: true
        });
      }
    }

    return days;
  }, [activeTimeEntries, currentUser?.id, confirmedDays, isHoliday]);

  // ============================================================================
  // FIX 1a & 1b: ALLOCATED PROJECTS LOGIC
  // ============================================================================
  
  /**
   * Get allocated projects for a specific user based on allocations.
   * This restores the pre-integration behavior where the picker and ring
   * only show projects the user is allocated to.
   * 
   * UPDATED: In Supabase mode, floating users (no team) get all projects in the picker,
   * but users WITH a team only get allocated projects (empty if none).
   * The donut ring always uses resolvedAllocations regardless.
   */
  const getAllocatedProjectsForUser = useCallback((userId, date) => {
    // If no userId or date, return all projects (for testing/standalone mode)
    // BUT only if NOT in Supabase allocation mode (unless floating user)
    if (!userId || !date) {
      if (useSupabaseAllocations) {
        // Floating users get all projects, team users get empty
        return isFloatingUser ? activeProjects : [];
      }
      return activeProjects;
    }

    const weekStart = getWeekStartLocalMonday(date);
    const userIdStr = String(userId);

    // Find allocations for this user in the week containing the date
    const userAllocations = resolvedAllocations.filter(
      a => String(a.userId) === userIdStr && String(a.weekStart) === weekStart
    );

    // Get unique project IDs from allocations
    const allocatedProjectIds = new Set(userAllocations.map(a => String(a.projectId)));

    // Also include projects the user has ever logged time on (from Supabase query),
    // but only if still active — imported entries on inactive projects should not
    // resurface those projects in the picker.
    const activeProjectIdSet = new Set(activeProjects.filter(p => p.status === 'active').map(p => String(p.id)));
    for (const pid of historicalProjectIds) {
      if (activeProjectIdSet.has(pid)) allocatedProjectIds.add(pid);
    }

    // If user has allocations or historical entries, filter to those projects
    if (allocatedProjectIds.size > 0) {
      return activeProjects.filter(p => allocatedProjectIds.has(String(p.id)));
    }

    // No allocations or history found - behavior depends on mode and team status
    if (useSupabaseAllocations) {
      // FLOATING USER EXCEPTION: Users without a team get all projects in the picker
      // This allows them to log time even without team-based allocations
      if (isFloatingUser) {
        return activeProjects;
      }
      // Team users with no allocations: return empty (strict mode)
      return [];
    }

    // LOCAL mode: return all projects for backwards compatibility
    return activeProjects;
  }, [useSupabaseAllocations, isFloatingUser, resolvedAllocations, activeProjects, historicalProjectIds]);

  /**
   * Memoized list of allocated projects for the current user/date.
   * - Sorted alphabetically by project name (FIX 4)
   * - Filtered to only allocated projects (FIX 1a)
   */
  const allocatedProjectsForPicker = useMemo(() => {
    const relevantUserId = currentUser?.id;
    const relevantDate = activeDate || new Date().toISOString().split('T')[0];
    
    const allocated = getAllocatedProjectsForUser(relevantUserId, relevantDate);
    
    // Sort alphabetically by project code (case-insensitive), falling back to name
    return [...allocated].sort((a, b) =>
      (a.projectCode || a.name || '').localeCompare(b.projectCode || b.name || '', undefined, { sensitivity: 'base' })
    );
  }, [currentUser?.id, activeDate, getAllocatedProjectsForUser]);

  const getAllocationsForDate = useCallback((date) => {
    const weekStart = getWeekStartLocalMonday(date);
    const userIdStr = String(currentUser?.id);
    
    const userAllocations = resolvedAllocations.filter(
      a => String(a.userId) === userIdStr && String(a.weekStart) === weekStart
    );
    
    return userAllocations.map(alloc => {
      const project = activeProjects.find(p => String(p.id) === String(alloc.projectId));
      return {
        ...alloc,
        project,
        dailyHours: alloc.allocatedHours / 5
      };
    });
  }, [currentUser?.id, resolvedAllocations, activeProjects]);

  const getEntriesForDate = useCallback((date) => {
    const userIdStr = String(currentUser?.id);
    return activeTimeEntries.filter(e => e.date === date && String(e.userId) === userIdStr);
  }, [currentUser?.id, activeTimeEntries]);

  const formatHours = (hours) => {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  };

  // Detect if the active date is a site visit day (unsaved or saved)
  const isSiteVisitDay = useMemo(() => {
    // Check unsaved entries
    if (currentEntries.some(e => e.taskId === SITE_VISIT_TASK_ID)) return true;
    // Check saved entries
    if (!activeDate) return false;
    const savedEntries = activeTimeEntries.filter(
      e => e.date === activeDate && String(e.userId) === String(currentUser?.id)
    );
    return savedEntries.some(
      e => String(e.taskId) === String(SITE_VISIT_CC_TASK_ID) && e.description === SITE_VISIT_DESCRIPTION
    );
  }, [currentEntries, activeDate, activeTimeEntries, currentUser?.id]);

  // ============================================================================
  // INITIALIZATION
  // ============================================================================
  
  // Load confirmed days from localStorage ONCE on mount
  useEffect(() => {
    const storedConfirmed = localStorage.getItem('tt_confirmed_days');
    if (storedConfirmed) {
      try {
        const parsed = JSON.parse(storedConfirmed);
        // Only update if different to avoid unnecessary re-renders
        setConfirmedDays(prev => {
          if (JSON.stringify(prev) !== JSON.stringify(parsed)) {
            return parsed;
          }
          return prev;
        });
      } catch (e) {
        console.error('Error loading confirmed days:', e);
      }
    }
  }, []); // Empty deps - only run once on mount

  // Set initial activeDate (separate effect to avoid loop with getPendingDays)
  useEffect(() => {
    // Only set activeDate if not already set
    if (!activeDate) {
      const pending = getPendingDays();
      if (pending.length > 0) {
        setActiveDate(pending[0].date);
      } else {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        setActiveDate(yesterday.toISOString().split('T')[0]);
      }
    }
  }, [activeDate, getPendingDays]);

  // Update selected project when allocated projects change
  useEffect(() => {
    if (allocatedProjectsForPicker.length > 0) {
      // If current selection is not in allocated projects, reset to first
      const currentInList = allocatedProjectsForPicker.some(
        p => String(p.id) === String(selectedProject)
      );
      if (!currentInList) {
        setSelectedProject(allocatedProjectsForPicker[0].id);
      }
    } else if (useSupabaseAllocations && !isFloatingUser) {
      // Supabase mode with team but no allocations: clear selection
      setSelectedProject(null);
    } else if (!selectedProject && activeProjects.length > 0) {
      // LOCAL mode OR floating user: fall back to activeProjects if no allocations
      setSelectedProject(activeProjects[0].id);
    }
  }, [allocatedProjectsForPicker, selectedProject, activeProjects, useSupabaseAllocations, isFloatingUser]);

  // ── Resolve selected project object (for stage allocation lookup) ──
  // activeProjects already carries full data including stageTimeline & budgetAllocation
  // (Supabase hook maps stage_timeline → stageTimeline, budget_allocation → budgetAllocation)
  const selectedProjectObj = useMemo(() => {
    if (!selectedProject) return null;
    return activeProjects.find(p => String(p.id) === String(selectedProject)) || null;
  }, [selectedProject, activeProjects]);

  // ── Stage-key mapping (matches InvoicesPage / TimelinePage DEFAULT_ID_TO_KEY) ──
  const DEFAULT_ID_TO_KEY = useMemo(() => ({
    1: 'advance', 2: 'cd_ad', 3: 'cd_id', 4: 'sd_ad', 5: 'sd_id',
    6: 'dd_ad', 7: 'dd_id', 8: 'aoc_ad', 9: 'aoc_id', 10: 'td_ad', 11: 'td_id',
    12: 'gfc1', 13: 'gfc2', 14: 'gfc3', 15: 'completion', 16: 'cc',
  }), []);

  // ── Build set of invoiced stage keys for the selected project ──
  // A stage is considered "invoiced" if it has any issued or paid invoice.
  const invoicedStageKeys = useMemo(() => {
    if (!selectedProjectObj) return new Set();
    const projectId = selectedProjectObj.id;
    const keys = new Set();
    (invoices || []).forEach(inv => {
      if (inv.projectId === projectId && inv.stageKey && (inv.status === 'issued' || inv.status === 'paid') && !inv.deletedAt) {
        keys.add(inv.stageKey);
      }
    });
    return keys;
  }, [selectedProjectObj, invoices]);

  // ── Filtered tasks for the wheel picker ──
  // Uses the same allocation detection as InvoicesPage's "Allocated only" filter:
  //   tl.invoicePercent > 0  OR  tl.billing.percent > 0  OR
  //   tl.billing.lumpsum/lumpSum > 0  OR  budgetAllocation[key] > 0
  //   OR any partStage has percentOfStage > 0 or lumpsum/lumpSum > 0
  // CC stage is always visible.
  // Stages with issued/paid invoices are excluded.
  // Only falls back to activeTasks if allocation data structures don't exist at all.
  const tasksForPicker = useMemo(() => {
    if (!selectedProjectObj) return activeTasks;

    // Safe number helper (matches InvoicesPage n(v) pattern)
    const n = (v) => Number(v ?? 0) || 0;

    // Read allocation structures (camelCase is canonical after Supabase mapping,
    // but check snake_case defensively in case of unmapped payloads)
    const timeline = selectedProjectObj.stageTimeline
      || selectedProjectObj.stage_timeline || null;
    const budgetAlloc = selectedProjectObj.budgetAllocation
      || selectedProjectObj.budget_allocation || null;

    // If the project has NO allocation data structures at all, we can't evaluate —
    // fall back to full activeTasks (project details not loaded yet)
    const hasAllocationData = timeline != null || budgetAlloc != null;

    if (!hasAllocationData) {
      return activeTasks;
    }

    const tl = timeline || {};
    const ba = budgetAlloc || {};
    const hideCcAndSite = NO_CC_SITE_PROJECT_CODES.has(
      String(selectedProjectObj.projectCode || selectedProjectObj.project_code || '').toUpperCase()
    );

    const filtered = activeTasks.filter(task => {
      const stageKey = DEFAULT_ID_TO_KEY[task.id] || `custom_${task.id}`;

      // CC stage always visible (matches InvoicesPage: stage.key === 'cc'),
      // except on the internal projects that have no site.
      if (stageKey === 'cc') return !hideCcAndSite;

      // A stage flagged for hour logging on this project is offered without any
      // billing allocation (internal stages such as BRG's "BRIO VOF").
      if (tl[stageKey]?.trackerStage === true) return true;

      // Exclude stages that have already been invoiced (issued or paid)
      if (invoicedStageKeys.has(stageKey)) return false;

      const stageTl = tl[stageKey] || {};
      const billing = stageTl.billing || {};

      // 1. invoicePercent (set by InvoicesPage when billing % changes)
      const hasInvPct = n(stageTl.invoicePercent) > 0;

      // 2. billing.percent (stage % of base design fee)
      const hasBillingPct = n(billing.percent) > 0;

      // 3. billing.lumpsum (check both camelCase variants)
      const hasLumpsum = n(billing.lumpsum ?? billing.lumpSum) > 0;

      // 4. budgetAllocation[stageKey] (legacy/fallback allocation %)
      const hasBudgetAlloc = n(ba[stageKey]) > 0;

      // 5. partStages: if any child part stage is allocated, parent counts as allocated
      const partStages = stageTl.partStages || [];
      const hasAllocatedPart = partStages.some(part => {
        const pb = part.billing || {};
        return n(pb.percentOfStage) > 0 || n(pb.lumpsum ?? pb.lumpSum) > 0;
      });

      return hasInvPct || hasBillingPct || hasLumpsum || hasBudgetAlloc || hasAllocatedPart;
    });

    // Return filtered list (may be empty — wheel handles that with an explicit UI state)
    // Append virtual SITE task so it appears for every project but the internal ones
    if (hideCcAndSite) return filtered;
    return [...filtered, { id: SITE_VISIT_TASK_ID, name: 'SITE' }];
  }, [selectedProjectObj, activeTasks, DEFAULT_ID_TO_KEY, invoicedStageKeys]);

  // Initialize selected task from filtered list
  useEffect(() => {
    if (!selectedTask && tasksForPicker.length > 0) {
      setSelectedTask(tasksForPicker[0].id);
    }
  }, [tasksForPicker, selectedTask]);

  // Selection safety: if current task is no longer in filtered list, reset
  // (including when list becomes empty → selectedTask becomes null)
  useEffect(() => {
    if (!selectedTask) return;
    const stillValid = tasksForPicker.some(t => String(t.id) === String(selectedTask));
    if (!stillValid) {
      setSelectedTask(tasksForPicker[0]?.id ?? null);
    }
  }, [tasksForPicker, selectedTask]);

  // ── Attach scrollend listeners (with timer fallback for Safari < 17.4) ──
  // IMPORTANT: This useEffect must appear AFTER allocatedProjectsForPicker and tasksForPicker
  // are defined, since they appear in the dependency array.
  useEffect(() => {
    const supportsScrollEnd = 'onscrollend' in window;

    const attachWheel = (ref, items, setSelected, timerRef, clickGuardRef) => {
      const el = ref.current;
      if (!el) return () => {};

      const onSettle = () => settleWheel(el, items, setSelected, clickGuardRef);

      if (supportsScrollEnd) {
        el.addEventListener('scrollend', onSettle, { passive: true });
        return () => el.removeEventListener('scrollend', onSettle);
      }
      // Fallback: debounced scroll with idle timeout + rAF for accuracy
      const onScroll = () => {
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
          requestAnimationFrame(onSettle);
        }, 120);
      };
      el.addEventListener('scroll', onScroll, { passive: true });
      return () => {
        el.removeEventListener('scroll', onScroll);
        clearTimeout(timerRef.current);
      };
    };

    const detachProject = attachWheel(
      projectWheelRef, allocatedProjectsForPicker, setSelectedProject,
      projectScrollTimerRef, projectClickGuardRef
    );
    const detachTask = attachWheel(
      taskWheelRef, tasksForPicker, setSelectedTask,
      taskScrollTimerRef, taskClickGuardRef
    );

    return () => { detachProject(); detachTask(); };
  }, [allocatedProjectsForPicker, tasksForPicker, settleWheel]);

  // Persist confirmed days to localStorage (UI state only)
  useEffect(() => {
    if (confirmedDays.length > 0) {
      localStorage.setItem('tt_confirmed_days', JSON.stringify(confirmedDays));
    }
  }, [confirmedDays]);

  // ============================================================================
  // DONUT CALCULATIONS
  // ============================================================================
  
  const calculateDonutSegments = useCallback(() => {
    if (!activeDate) return { inner: [], outer: [] };

    const entries = getEntriesForDate(activeDate);
    const allocs = getAllocationsForDate(activeDate);

    // FIX 1b: Get allocated project IDs for filtering outer ring
    const allocatedProjectIds = allocs.map(a => String(a.projectId));

    let totalLogged = 0;
    const innerSegments = [];

    // ── Site Visit: check if this is a site visit day (saved or unsaved) ──
    const savedSiteVisit = entries.find(
      e => String(e.taskId) === String(SITE_VISIT_CC_TASK_ID) && e.description === SITE_VISIT_DESCRIPTION
    );
    const unsavedSiteVisit = currentEntries.find(e => e.taskId === SITE_VISIT_TASK_ID);
    const siteVisitEntry = unsavedSiteVisit || savedSiteVisit;

    if (siteVisitEntry) {
      // Site visit day: single segment filling 100% of the donut
      const projId = siteVisitEntry.projectId;
      const project = activeProjects.find(p => String(p.id) === String(projId));
      innerSegments.push({
        entryId: savedSiteVisit?.id,
        tempId: unsavedSiteVisit?.tempId,
        projectId: projId,
        taskId: unsavedSiteVisit ? SITE_VISIT_TASK_ID : SITE_VISIT_CC_TASK_ID,
        project,
        task: { id: SITE_VISIT_TASK_ID, name: 'SITE' },
        hours: siteVisitEntry.hours || (siteVisitEntry.duration / 3600),
        duration: savedSiteVisit?.duration,
        billable: siteVisitEntry.billable,
        color: project?.color || '#64748b',
        label: `${project?.projectCode || '??'} - SITE`,
        saved: !!savedSiteVisit && !unsavedSiteVisit,
        savedIndex: savedSiteVisit ? 0 : undefined,
        currentIndex: unsavedSiteVisit ? 0 : undefined,
        isSiteVisit: true,
        startAngle: 0,
        sweepAngle: 359.99,
        endAngle: 359.99
      });

      // Still compute outer ring normally
      let totalAllocated = 0;
      const outerSegments = [];
      allocs.forEach(alloc => {
        if (allocatedProjectIds.includes(String(alloc.projectId))) {
          totalAllocated += alloc.dailyHours;
          outerSegments.push({
            projectId: alloc.projectId,
            project: alloc.project,
            hours: alloc.dailyHours,
            color: alloc.project?.color || '#64748b',
            label: `${alloc.project?.projectCode || '??'}: ${formatHours(alloc.dailyHours)}`
          });
        }
      });
      let outerAngle = 0;
      outerSegments.forEach(seg => {
        seg.startAngle = outerAngle;
        seg.sweepAngle = (seg.hours / Math.max(totalAllocated, 12)) * 360;
        seg.endAngle = outerAngle + seg.sweepAngle;
        outerAngle = seg.endAngle;
      });

      return { inner: innerSegments, outer: outerSegments };
    }

    entries.forEach((entry, idx) => {
      const project = activeProjects.find(p => String(p.id) === String(entry.projectId));
      const task = activeTasks.find(t => String(t.id) === String(entry.taskId));
      const hours = entry.duration / 3600;
      totalLogged += hours;

      innerSegments.push({
        entryId: entry.id,
        projectId: entry.projectId,
        taskId: entry.taskId,
        project,
        task,
        hours,
        duration: entry.duration, // Keep original duration for updates
        billable: entry.billable,
        color: project?.color || '#64748b',
        label: `${project?.projectCode || '??'} - ${task?.name || '??'}`,
        saved: true,
        savedIndex: idx // Index in the saved entries array
      });
    });

    currentEntries.forEach((entry, idx) => {
      const project = activeProjects.find(p => String(p.id) === String(entry.projectId));
      const task = activeTasks.find(t => String(t.id) === String(entry.taskId));
      totalLogged += entry.hours;

      innerSegments.push({
        tempId: entry.tempId,
        projectId: entry.projectId,
        taskId: entry.taskId,
        project,
        task,
        hours: entry.hours,
        billable: entry.billable,
        color: project?.color || '#64748b',
        label: `${project?.projectCode || '??'} - ${task?.name || '??'}`,
        saved: false,
        currentIndex: idx
      });
    });

    const maxHours = Math.max(totalLogged, 12);
    let currentAngle = 0;

    innerSegments.forEach(seg => {
      seg.startAngle = currentAngle;
      seg.sweepAngle = (seg.hours / maxHours) * 360;
      seg.endAngle = currentAngle + seg.sweepAngle;
      currentAngle = seg.endAngle;
    });

    // Outer donut - 10% thickness
    // FIX 1b: Only show allocated projects in outer ring
    let totalAllocated = 0;
    const outerSegments = [];

    allocs.forEach(alloc => {
      // Ensure this allocation's project is in the allocated list
      if (allocatedProjectIds.includes(String(alloc.projectId))) {
        totalAllocated += alloc.dailyHours;
        outerSegments.push({
          projectId: alloc.projectId,
          project: alloc.project,
          hours: alloc.dailyHours,
          color: alloc.project?.color || '#64748b',
          label: `${alloc.project?.projectCode || '??'}: ${formatHours(alloc.dailyHours)}`
        });
      }
    });

    currentAngle = 0;
    outerSegments.forEach(seg => {
      seg.startAngle = currentAngle;
      seg.sweepAngle = (seg.hours / Math.max(totalAllocated, 12)) * 360;
      seg.endAngle = currentAngle + seg.sweepAngle;
      currentAngle = seg.endAngle;
    });

    return { inner: innerSegments, outer: outerSegments };
  }, [activeDate, getEntriesForDate, getAllocationsForDate, activeProjects, activeTasks, currentEntries]);

  // ============================================================================
  // ENTRY MANAGEMENT
  // ============================================================================
  
  // FIX B2: handleAddEntry accepts optional overrides so callers can pass the ID
  // they just set via setState (which React batches, so reading from state would be stale).
  const handleAddEntry = useCallback((overrideProjectId, overrideTaskId) => {
    const projId = overrideProjectId ?? selectedProject;
    const taskId = overrideTaskId ?? selectedTask;

    if (!projId || !taskId) {
      // Provide more helpful message when no allocations in Supabase mode
      if (useSupabaseAllocations && allocatedProjectsForPicker.length === 0) {
        alert('No projects allocated for this week. Please set allocations in the Focus Board.');
      } else {
        alert('Please scroll to select both project and stage');
      }
      return;
    }

    // ── Site Visit handling ──
    if (taskId === SITE_VISIT_TASK_ID) {
      // Block if saved entries already exist for this day
      const savedEntries = activeDate ? getEntriesForDate(activeDate) : [];
      const hasSavedNonSiteEntries = savedEntries.some(
        e => !(String(e.taskId) === String(SITE_VISIT_CC_TASK_ID) && e.description === SITE_VISIT_DESCRIPTION)
      );
      if (hasSavedNonSiteEntries || (currentEntries.length > 0 && !currentEntries.some(e => e.taskId === SITE_VISIT_TASK_ID))) {
        alert('Cannot add site visit — this day already has time entries. Clear them first.');
        return;
      }
      // Duplicate guard for site visit
      if (currentEntries.some(e => e.taskId === SITE_VISIT_TASK_ID)) return;

      // Replace all unsaved entries with a single site visit entry
      const siteEntry = {
        tempId: crypto.randomUUID ? crypto.randomUUID() : `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        projectId: projId,
        taskId: SITE_VISIT_TASK_ID,
        hours: siteVisitBackendHours,
        billable: isProjectBillable(projId),
        isSiteVisit: true
      };
      setCurrentEntries([siteEntry]);
      if (navigator.vibrate) navigator.vibrate(20);
      return;
    }

    // Block adding regular entries on a site visit day
    if (isSiteVisitDay) {
      alert('This day is logged as a site visit. Remove the site visit first to add other entries.');
      return;
    }

    // FIX B2: Duplicate guard — don't create a new entry if the most recent unsaved entry
    // has the same projectId + taskId and is still at the default 0.5h (user hasn't adjusted it)
    const lastEntry = currentEntries[currentEntries.length - 1];
    if (lastEntry &&
        String(lastEntry.projectId) === String(projId) &&
        String(lastEntry.taskId) === String(taskId) &&
        lastEntry.hours === 0.5) {
      return;
    }

    const newEntry = {
      tempId: crypto.randomUUID ? crypto.randomUUID() : `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      projectId: projId,
      taskId: taskId,
      hours: 0.5,
      billable: isProjectBillable(projId)
    };

    setCurrentEntries(prev => [...prev, newEntry]);

    if (navigator.vibrate) {
      navigator.vibrate(20);
    }
  }, [selectedProject, selectedTask, useSupabaseAllocations, allocatedProjectsForPicker.length, currentEntries, activeDate, getEntriesForDate, isSiteVisitDay, siteVisitBackendHours, isProjectBillable]);

  const adjustEntryHours = (index, newHours) => {
    // Guard: if index is invalid or entry doesn't exist, return early
    if (index == null || index < 0 || index >= currentEntries.length) return;
    const entry = currentEntries[index];
    if (!entry) return;
    // Site visit hours are fixed — don't allow drag adjustment
    if (entry.taskId === SITE_VISIT_TASK_ID || entry.isSiteVisit) return;

    const updated = [...currentEntries];
    updated[index].hours = Math.max(0.5, Math.round(newHours * 2) / 2);
    setCurrentEntries(updated);
  };

  const _toggleEntryBillable = (index) => { // eslint-disable-line no-unused-vars
    // Guard: if index is invalid or entry doesn't exist, return early
    if (index == null || index < 0 || index >= currentEntries.length) return;
    const entry = currentEntries[index];
    if (!entry) return;
    
    const updated = [...currentEntries];
    updated[index].billable = !updated[index].billable;
    setCurrentEntries(updated);
  };

  /**
   * Handle pointer up on segment - implements manual double-tap detection.
   * For saved segments (with entryId/id): triggers onDeleteTimeEntry with optimistic update
   * For unsaved segments (with tempId only): removes from local currentEntries state
   * Two taps on the same segment within 350ms triggers the action.
   * Includes HARD guard to prevent repeated delete calls for the same entry.
   */
  const handleSegmentPointerUp = useCallback((e, seg) => {
    // Prevent event bubbling
    e.preventDefault();
    e.stopPropagation();
    
    const now = Date.now();
    const last = lastSavedClickRef.current;
    
    // Determine if this is a saved entry by checking for a real DB id
    // Priority: entryId > id (both are DB identifiers)
    const dbId = seg.entryId ?? seg.id;
    const isSaved = dbId != null && dbId !== '';
    
    // Use dbId for saved entries, tempId for drafts
    const segmentId = isSaved ? dbId : seg.tempId;
    if (!segmentId) return;
    
    // Check if this is a double-tap (same segment, within 350ms)
    if (last.entryId === segmentId && (now - last.time) < 350) {
      // Reset immediately to prevent triple-tap
      lastSavedClickRef.current = { entryId: null, time: 0 };
      
      // Cancel any active drag when deleting to prevent crash
      setIsDragging(false);
      setDragStartAngle(null);
      setDragSegmentIndex(null);
      setDragStartHours(null);
      draggingSavedSegmentRef.current = null;
      
      if (isSaved) {
        // HARD GUARD: check if already deleting this entry - if so, return immediately
        if (deletingSavedIdsRef.current.has(dbId)) {
          return;
        }
        
        // Add to deleting set IMMEDIATELY - will only be removed when entry disappears or after long timeout
        deletingSavedIdsRef.current.add(dbId);
        
        // Trigger delete for saved entry via Supabase
        if (onDeleteTimeEntry) {
          onDeleteTimeEntry(dbId);
        }
        
        // Long timeout to remove from set (5 seconds) - only as fallback
        setTimeout(() => {
          deletingSavedIdsRef.current.delete(dbId);
        }, 5000);
        
        return;
      } else if (seg.tempId) {
        // Remove unsaved entry from local state immediately
        setCurrentEntries(prev => prev.filter(e => e.tempId !== seg.tempId));
        return;
      }
    } else {
      // First tap - store for potential double-tap
      lastSavedClickRef.current = { entryId: segmentId, time: now };
    }
  }, [onDeleteTimeEntry]);

  // Note: removeEntry is kept for potential future use but currently not wired to UI
  // eslint-disable-next-line no-unused-vars
  const removeEntry = useCallback((index) => {
    setCurrentEntries(prev => prev.filter((_, i) => i !== index));
  }, []);

  // ============================================================================
  // DRAG TO ADJUST
  // ============================================================================
  
  const getAngleFromPoint = (x, y, centerX, centerY) => {
    const dx = x - centerX;
    const dy = y - centerY;
    let angle = Math.atan2(dy, dx) * (180 / Math.PI);
    angle = (angle + 90 + 360) % 360;
    return angle;
  };

  // Legacy handler for unsaved segments (kept for compatibility)
  const _handleSegmentPointerDown = (e, segmentIndex) => { // eslint-disable-line no-unused-vars
    const svg = svgRef.current;
    if (!svg) return;
    
    // Guard: ensure the entry exists before starting drag
    if (segmentIndex == null || segmentIndex < 0 || segmentIndex >= currentEntries.length) return;
    if (!currentEntries[segmentIndex]) return;
    
    const rect = svg.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    
    const angle = getAngleFromPoint(e.clientX, e.clientY, centerX, centerY);

    setIsDragging(true);
    setDragStartAngle(angle);
    setDragSegmentIndex(segmentIndex);
    setDragStartHours(currentEntries[segmentIndex].hours);
    draggingSavedSegmentRef.current = null; // Not a saved segment
    dragPrevAngleRef.current = angle;
    dragAccumulatedDeltaRef.current = 0;
    
    e.currentTarget.setPointerCapture(e.pointerId);
    
    if (navigator.vibrate) {
      navigator.vibrate(10);
    }
    
    e.preventDefault();
    e.stopPropagation();
  };

  /**
   * Unified pointer down handler for both saved and unsaved segments.
   * Saved segments: tracks entryId and original hours for persisting on release
   * Unsaved segments: uses currentIndex for local state updates
   */
  const handleSegmentPointerDownUnified = (e, seg, segmentListIndex) => {
    const svg = svgRef.current;
    if (!svg) return;
    
    const rect = svg.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    
    const angle = getAngleFromPoint(e.clientX, e.clientY, centerX, centerY);
    
    if (seg.saved) {
      // Saved segment - track for persisting on release
      setIsDragging(true);
      setDragStartAngle(angle);
      setDragSegmentIndex(segmentListIndex); // Index in innerSegments array
      setDragStartHours(seg.hours);
      draggingSavedSegmentRef.current = {
        entryId: seg.entryId,
        originalHours: seg.hours,
        originalDuration: seg.duration
      };
    } else if (seg.currentIndex != null) {
      // Unsaved segment - use existing logic
      if (seg.currentIndex < 0 || seg.currentIndex >= currentEntries.length) return;
      if (!currentEntries[seg.currentIndex]) return;

      setIsDragging(true);
      setDragStartAngle(angle);
      setDragSegmentIndex(seg.currentIndex);
      setDragStartHours(seg.hours);
      draggingSavedSegmentRef.current = null;
    } else {
      return;
    }

    dragPrevAngleRef.current = angle;
    dragAccumulatedDeltaRef.current = 0;

    e.currentTarget.setPointerCapture(e.pointerId);
    
    if (navigator.vibrate) {
      navigator.vibrate(10);
    }
    
    e.preventDefault();
    e.stopPropagation();
  };

  const handlePointerMove = (e) => {
    // Guard: not dragging or no active segment
    if (!isDragging || dragSegmentIndex === null) return;
    
    const savedDrag = draggingSavedSegmentRef.current;
    
    if (savedDrag) {
      // Dragging a SAVED segment - update visually but don't persist yet
      // The visual update will happen through re-render when we update hours
      // For now, we just track the new hours value
      const svg = svgRef.current;
      if (!svg) return;
      
      const rect = svg.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      
      const currentAngle = getAngleFromPoint(e.clientX, e.clientY, centerX, centerY);
      let frameDelta = currentAngle - dragPrevAngleRef.current;
      if (frameDelta > 180) frameDelta -= 360;
      if (frameDelta < -180) frameDelta += 360;
      dragPrevAngleRef.current = currentAngle;
      dragAccumulatedDeltaRef.current += frameDelta;

      const hoursDelta = (dragAccumulatedDeltaRef.current / 360) * 12;
      const newHours = Math.max(0.5, Math.round((dragStartHours + hoursDelta) * 2) / 2);

      // Update the saved segment's hours optimistically for visual feedback
      const newDuration = Math.round(newHours * 3600);
      if (setTimeEntries) {
        setTimeEntries(prev => prev.map(te =>
          te.id === savedDrag.entryId ? { ...te, duration: newDuration } : te
        ));
      }
    } else {
      // Dragging an UNSAVED segment
      // Guard: entry was deleted during drag - reset drag state
      if (dragSegmentIndex < 0 || dragSegmentIndex >= currentEntries.length || !currentEntries[dragSegmentIndex]) {
        setIsDragging(false);
        setDragStartAngle(null);
        setDragSegmentIndex(null);
        setDragStartHours(null);
        return;
      }

      const svg = svgRef.current;
      if (!svg) return;

      const rect = svg.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;

      const currentAngle = getAngleFromPoint(e.clientX, e.clientY, centerX, centerY);
      let frameDelta = currentAngle - dragPrevAngleRef.current;
      if (frameDelta > 180) frameDelta -= 360;
      if (frameDelta < -180) frameDelta += 360;
      dragPrevAngleRef.current = currentAngle;
      dragAccumulatedDeltaRef.current += frameDelta;

      const hoursDelta = (dragAccumulatedDeltaRef.current / 360) * 12;
      const newHours = dragStartHours + hoursDelta;

      adjustEntryHours(dragSegmentIndex, newHours);
    }
    
    e.preventDefault();
  };

  const handlePointerUp = (e) => {
    if (isDragging) {
      const savedDrag = draggingSavedSegmentRef.current;
      
      if (savedDrag && onUpdateTimeEntry) {
        // Use accumulated delta from pointermove frames — avoids the 180° snap-back issue
        const hoursDelta = (dragAccumulatedDeltaRef.current / 360) * 12;
        const newHours = Math.max(0.5, Math.round((dragStartHours + hoursDelta) * 2) / 2);
        const newDuration = Math.round(newHours * 3600);

        // Only persist if hours actually changed
        if (newDuration !== savedDrag.originalDuration) {
          onUpdateTimeEntry(savedDrag.entryId, { duration: newDuration });
        }
      }

      setIsDragging(false);
      setDragStartAngle(null);
      setDragSegmentIndex(null);
      setDragStartHours(null);
      draggingSavedSegmentRef.current = null;
      dragPrevAngleRef.current = null;
      dragAccumulatedDeltaRef.current = 0;
      
      e.preventDefault();
    }
  };

  // ============================================================================
  // CONFIRM DAY - Saves entries via callbacks (Supabase or local)
  // ============================================================================
  
  // Wrapped in useCallback to satisfy ESLint exhaustive-deps
  const moveToNextPendingDay = useCallback((justConfirmedDate) => {
    const pending = getPendingDays().filter(day => day.date !== justConfirmedDate);
    if (pending.length > 0) {
      setActiveDate(pending[0].date);
    } else {
      const today = new Date();
      setActiveDate(today.toISOString().split('T')[0]);
    }
  }, [getPendingDays]);

  const handleConfirmDay = useCallback(async () => {
    if (!activeDate) return;
    if (confirmGuardRef.current) return;
    confirmGuardRef.current = true;

    const dateToConfirm = activeDate;

    // Allow confirming with zero entries (weekends, holidays, sick days, no work days)
    if (currentEntries.length === 0) {
      setIsConfirming(true);
      setConfirmedDays(prev => [...prev, dateToConfirm]);

      setTimeout(() => {
        moveToNextPendingDay(dateToConfirm);
        setIsConfirming(false);
        confirmGuardRef.current = false;
      }, 2000);
      return;
    }

    // Has entries - save them
    setIsConfirming(true);

    // FIX B2: Snapshot currentEntries at confirm-time to avoid stale closure reads
    const entriesToSave = [...currentEntries];

    // Build entry objects with Math.round for clean integer durations
    // Site visit entries: map virtual SITE task to CC (task_id=16) with configured hours
    const newEntries = entriesToSave.map(e => {
      const isSite = e.taskId === SITE_VISIT_TASK_ID || e.isSiteVisit;
      return {
        userId: currentUser.id,
        projectId: e.projectId,
        taskId: isSite ? SITE_VISIT_CC_TASK_ID : e.taskId,
        description: isSite ? SITE_VISIT_DESCRIPTION : 'Logged via Donut Tracker',
        duration: isSite ? Math.round(siteVisitBackendHours * 3600) : Math.round(e.hours * 3600),
        date: dateToConfirm,
        billable: e.billable,
        notes: '',
        approved: false
      };
    });
    
    // Save via appropriate method based on mode
    if (useSupabaseDB && onCreateTimeEntry) {
      // Supabase mode - create entries one by one via callback
      // The hook's createTimeEntry() optimistically adds each inserted row
      // to its internal state, so activeTimeEntries updates in real-time.
      let allSucceeded = true;
      for (const entry of newEntries) {
        try {
          const result = await onCreateTimeEntry(entry);
          if (!result.success) {
            console.error('[Donut] Failed to create time entry:', result.error);
            allSucceeded = false;
          }
        } catch (err) {
          console.error('[Donut] Error creating time entry:', err);
          allSucceeded = false;
        }
      }
      
      // NOTE: Do NOT refetch after creating entries.
      // createTimeEntry() already optimistically adds each entry to hook state.
      // A full refetch would replace state with a limited window (top 1000 by date),
      // potentially excluding the new entry if its auto-generated serial ID is lower
      // than old migrated bigint IDs. The optimistic state is correct and sufficient.
    } else if (setTimeEntries) {
      // Local mode - add entries with generated IDs
      const entriesWithIds = newEntries.map(e => ({
        ...e,
        id: Date.now() + Math.random()
      }));
      setTimeEntries(prev => [...prev, ...entriesWithIds]);
    }
    
    setCurrentEntries([]);
    setConfirmedDays(prev => [...prev, dateToConfirm]);
    
    setTimeout(() => {
      moveToNextPendingDay(dateToConfirm);
      setIsConfirming(false);
      confirmGuardRef.current = false;
    }, 2000);
  }, [activeDate, currentEntries, currentUser?.id, useSupabaseDB, onCreateTimeEntry, setTimeEntries, moveToNextPendingDay, siteVisitBackendHours]);

  // ============================================================================
  // KEYBOARD SHORTCUTS
  // ============================================================================
  
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'Enter') {
        handleConfirmDay();
      }
    };
    
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [handleConfirmDay]);

  // ============================================================================
  // RENDER
  // ============================================================================
  
  const { inner: innerSegments, outer: outerSegments } = calculateDonutSegments();
  const pendingDays = getPendingDays();
  const completedDays = getCompletedDays();
  
  const today = new Date();
  const todayStr = today.toISOString().split('T')[0];
  const todayObj = {
    date: todayStr,
    dateObj: today,
    isWeekend: isWeekend(todayStr),
    isHoliday: isHoliday(todayStr),
    isToday: true,
    isPending: false
  };
  
  const allDays = [...completedDays.reverse(), ...pendingDays, todayObj];
  
  const canConfirm = !isConfirming;
  // Total logged hours for active date (always visible)
  const totalLoggedHours = useMemo(() => {
    if (!activeDate) return 0;
    const entries = getEntriesForDate(activeDate);
    const savedTotal = entries.reduce((sum, e) => sum + (e.duration / 3600), 0);
    const unsavedTotal = currentEntries.reduce((sum, e) => sum + (e.hours || 0), 0);
    return savedTotal + unsavedTotal;
  }, [activeDate, getEntriesForDate, currentEntries]);

  const donutRadius = 45.28;
  const calculateDayPosition = (index, total) => {
    const arcLength = 90;
    const centerAngle = 270;
    const startAngle = centerAngle - (arcLength / 2);
    const angle = startAngle + (index / Math.max(1, total - 1)) * arcLength;
    const radians = (angle * Math.PI) / 180;
    const x = 50 + donutRadius * Math.cos(radians);
    const y = 50 + donutRadius * Math.sin(radians);
    return { x, y };
  };

  // SVG arc path helper
  const describeArc = (cx, cy, innerR, outerR, startAngle, endAngle) => {
    const startRad = ((startAngle - 90) * Math.PI) / 180;
    const endRad = ((endAngle - 90) * Math.PI) / 180;
    
    const x1Inner = cx + innerR * Math.cos(startRad);
    const y1Inner = cy + innerR * Math.sin(startRad);
    const x2Inner = cx + innerR * Math.cos(endRad);
    const y2Inner = cy + innerR * Math.sin(endRad);
    
    const x1Outer = cx + outerR * Math.cos(startRad);
    const y1Outer = cy + outerR * Math.sin(startRad);
    const x2Outer = cx + outerR * Math.cos(endRad);
    const y2Outer = cy + outerR * Math.sin(endRad);
    
    const largeArc = endAngle - startAngle > 180 ? 1 : 0;
    
    return `M ${x1Inner} ${y1Inner}
            L ${x1Outer} ${y1Outer}
            A ${outerR} ${outerR} 0 ${largeArc} 1 ${x2Outer} ${y2Outer}
            L ${x2Inner} ${y2Inner}
            A ${innerR} ${innerR} 0 ${largeArc} 0 ${x1Inner} ${y1Inner}`;
  };

  return (
    <div
      ref={containerRef}
      className="flex flex-col items-center justify-center"
      style={{
        backgroundColor: '#F5F5F5',
        fontFamily: "'Inter', sans-serif",
        WebkitUserSelect: 'none',
        userSelect: 'none',
        WebkitTouchCallout: 'none',
        height: `calc(100vh - ${navHeight}px)`,
        overflow: 'hidden',
        padding: '0 8px',
        boxSizing: 'border-box'
      }}
    >
      {/* Top overlay bar: username (left) */}
      {showFullscreenHint && (
        <div
          onClick={() => {
            document.documentElement.requestFullscreen().then(() => setShowFullscreenHint(false)).catch(() => {});
          }}
          style={{
            position: 'fixed',
            top: 8,
            left: '50%',
            transform: 'translateX(-50%)',
            padding: '6px 16px',
            borderRadius: 20,
            background: 'rgba(0,0,0,0.7)',
            color: '#fff',
            fontSize: 12,
            cursor: 'pointer',
            zIndex: 100000,
            pointerEvents: 'auto'
          }}
        >
          Tap to enter fullscreen
        </div>
      )}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: '44px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 16px',
          zIndex: 99999,
          pointerEvents: 'none',
          background: 'transparent',
          fontFamily: "'Raleway', 'Inter', sans-serif"
        }}
      >
        {/* Info icon + current user name */}
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '13px',
            fontWeight: 500,
            color: '#666666',
            letterSpacing: '0.02em',
            pointerEvents: 'auto'
          }}
        >
          <span className="donut-info-trigger" style={{ cursor: 'help', display: 'inline-flex', opacity: 0.45, position: 'relative', flexShrink: 0 }}>
            <Info style={{ width: 14, height: 14 }} />
            <span className="donut-info-tip">
              <strong style={{ fontSize: '11px', display: 'block', marginBottom: '8px' }}>Donut Time Tracker</strong>
              <span>The Donut Time Tracker records the work you've done so effort across projects is properly captured. This helps everyone understand how time is spent and plan projects more effectively.</span>

              <strong style={{ fontSize: '10px', display: 'block', marginTop: '10px', marginBottom: '4px' }}>Daily routine</strong>
              <ul style={{ margin: '0 0 0 14px', padding: 0, listStyle: 'disc' }}>
                <li>Each morning, open the tracker to log the previous day, (or log at the end of the day)</li>
                <li>If you forgot, were sick, on leave, or need to correct something, you can edit the last 7 days using the date selector.</li>
              </ul>

              <strong style={{ fontSize: '10px', display: 'block', marginTop: '10px', marginBottom: '4px' }}>Logging time</strong>
              <ul style={{ margin: '0 0 0 14px', padding: 0, listStyle: 'disc' }}>
                <li>The tracker works one day at a time. The donut represents 12 hours.</li>
                <li>The outer ring shows your projects allocated for the week (averaged per day).</li>
                <li>Select a project using the project picker wheel on the left.</li>
                <li>Select the stage/task using the stage picker wheel.</li>
                <li>Click the stage selector to create a time segment.</li>
                <li>Drag the segment to adjust the time.</li>
                <li>Edit an entry by adjusting its segment.</li>
                <li>Delete an entry by double-clicking its segment.</li>
                <li>When finished, click Confirm Day (central ochre button) to save.</li>
              </ul>

              <p style={{ margin: '8px 0 0' }}>After confirming, the entries fade briefly and the tracker moves to the next day that still needs logging.</p>
              <p style={{ margin: '6px 0 0' }}>If you need to log more than 12 hours, spread time across another day or use the Time Entries page.</p>

              <strong style={{ fontSize: '10px', display: 'block', marginTop: '10px', marginBottom: '4px' }}>Date dots</strong>
              <ul style={{ margin: '0 0 0 14px', padding: 0, listStyle: 'disc' }}>
                <li><b>Normal</b> → weekday</li>
                <li><b>Light</b> → weekend or holiday</li>
                <li><b>Faded</b> → completed day</li>
                <li><b>Large</b> → yesterday (the day you normally fill in)</li>
              </ul>
            </span>
          </span>
          {currentUser?.name || ''}
        </span>
      </div>

      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .donut-info-tip {
          display: none;
          position: absolute;
          top: calc(100% + 6px);
          left: 0;
          width: 300px;
          padding: 14px 16px;
          background: #fff;
          color: #444;
          font-size: 10px;
          font-weight: 400;
          line-height: 1.55;
          white-space: normal;
          letter-spacing: 0;
          z-index: 200;
          pointer-events: none;
          border-radius: 4px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.14);
          font-family: 'Raleway', 'Inter', sans-serif;
        }
        .donut-info-trigger:hover .donut-info-tip {
          display: block;
        }
        .donut-info-trigger:hover {
          opacity: 1 !important;
        }
      `}</style>

      {/* Centered Donut */}
      <div
        className="relative transition-opacity duration-2000 flex-shrink-0"
        style={{
          width: `min(85vw, calc(100vh - ${navHeight + 160}px), 700px)`,
          height: `min(85vw, calc(100vh - ${navHeight + 160}px), 700px)`,
          opacity: isConfirming ? 0.8 : 1,
          transitionDuration: '2000ms'
        }}
      >
        {/* Radial Day Dots */}
        {allDays.map((day, idx) => {
          const isActive = day.date === activeDate;
          const isHovered = hoveredDay?.date === day.date;
          const pos = calculateDayPosition(idx, allDays.length);
          const dotSize = isActive ? 12 : day.isCompleted ? 6 : 8;
          
          // Day colors: weekday=#bcb16a, weekend/holiday=#d9d3aa (lighter tone), current=#9f902a, hover=#8f8226
          const isNonWorkDay = day.isWeekend || day.isHoliday;
          const dotColor = isActive
            ? '#9f902a'  // Current day (active selection)
            : day.isCompleted
            ? (isNonWorkDay ? '#f0ede0' : '#d9d3aa')  // Completed: lighter for weekends/holidays
            : isNonWorkDay
            ? '#ebe8d8'  // Weekend or holiday - lighter tone
            : '#bcb16a'; // Weekday - normal tone
          
          return (
            <React.Fragment key={day.date}>
              {/* Date label above dot */}
              {isHovered && (
                <span
                  style={{
                    position: 'absolute',
                    left: `${pos.x}%`,
                    top: `calc(${pos.y}% - 40px)`,
                    transform: 'translateX(-50%)',
                    color: '#000000',
                    fontSize: '10px',
                    fontWeight: 'bold',
                    textAlign: 'center',
                    pointerEvents: 'none',
                    zIndex: 20
                  }}
                >
                  {day.dateObj.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' })}
                </span>
              )}
              <button
                onClick={() => {
                  setActiveDate(day.date);
                  setCurrentEntries([]);
                }}
                onMouseEnter={() => setHoveredDay(day)}
                onMouseLeave={() => setHoveredDay(null)}
                className="absolute transition-all hover:scale-125 cursor-pointer flex items-center justify-center"
                style={{
                  left: `${pos.x}%`,
                  top: `${pos.y}%`,
                  width: `${dotSize}px`,
                  height: `${dotSize}px`,
                  backgroundColor: isHovered ? '#8f8226' : dotColor,  // Hover color: #8f8226
                  color: 'transparent',
                  borderRadius: '50%',
                  transform: 'translate(-50%, -50%)',
                  border: isActive ? '2px solid rgba(127, 115, 33, 0.3)' : 'none',
                  zIndex: isActive ? 10 : isHovered ? 8 : 5,
                  fontSize: '6px',
                  fontWeight: 'bold'
                }}
                title={day.dateObj.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' })}
              />
            </React.Fragment>
          );
        })}

        {/* SVG Donut */}
        <svg 
          ref={svgRef}
          viewBox="0 0 100 100" 
          className="w-full h-full"
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerUp}
          style={{ touchAction: 'none' }}
        >
          {/* Inner donut default background track */}
          <circle 
            cx="50" 
            cy="50" 
            r="27.5" 
            fill="none" 
            stroke="#bcb16a" 
            strokeWidth="15"
          />
          
          {/* Outer donut default background track */}
          <circle 
            cx="50" 
            cy="50" 
            r="38.25" 
            fill="none" 
            stroke="#bcb16a" 
            strokeWidth="2.5"
          />
          
          {/* Inner donut segments (logged time) - Project activity */}
          {innerSegments.map((seg, idx) => (
            <path
              key={`inner-${idx}`}
              d={describeArc(50, 50, 20, 35, seg.startAngle, seg.endAngle)}
              fill={seg.color}
              opacity={seg.saved ? 0.7 : 1}
              stroke="none"
              style={{ cursor: 'pointer', pointerEvents: 'auto' }}
              onMouseEnter={() => setHoveredSegment({ type: 'inner', index: idx, segment: seg })}
              onMouseLeave={() => setHoveredSegment(null)}
              onPointerDown={(e) => handleSegmentPointerDownUnified(e, seg, idx)}
              onPointerUp={(e) => handleSegmentPointerUp(e, seg)}
            />
          ))}

          {/* Boundary spokes between different segments */}
          {innerSegments.map((seg, idx) => {
            if (idx === 0) return null;
            const angle = seg.startAngle;
            const rad = ((angle - 90) * Math.PI) / 180;
            const x1 = 50 + 20 * Math.cos(rad);
            const y1 = 50 + 20 * Math.sin(rad);
            const x2 = 50 + 35 * Math.cos(rad);
            const y2 = 50 + 35 * Math.sin(rad);
            return (
              <line
                key={`boundary-${idx}`}
                x1={x1} y1={y1} x2={x2} y2={y2}
                stroke="#F5F5F5"
                strokeWidth="0.3"
                style={{ pointerEvents: 'none' }}
              />
            );
          })}

          {/* Half-hour spoke dividers inside segments */}
          {innerSegments.map((seg) => {
            if (seg.hours <= 0.5) return null;
            const maxHours = Math.max(innerSegments.reduce((s, x) => s + x.hours, 0), 12);
            const halfHourSweep = (0.5 / maxHours) * 360;
            const spokeAngles = [];
            let a = seg.startAngle + halfHourSweep;
            while (a < seg.endAngle - 0.5) {
              spokeAngles.push(a);
              a += halfHourSweep;
            }
            return spokeAngles.map((angle, si) => {
              const rad = ((angle - 90) * Math.PI) / 180;
              const x1 = 50 + 20 * Math.cos(rad);
              const y1 = 50 + 20 * Math.sin(rad);
              const x2 = 50 + 35 * Math.cos(rad);
              const y2 = 50 + 35 * Math.sin(rad);
              return (
                <line
                  key={`spoke-${seg.startAngle}-${si}`}
                  x1={x1} y1={y1} x2={x2} y2={y2}
                  stroke="#F5F5F5"
                  strokeWidth="0.3"
                  style={{ pointerEvents: 'none' }}
                />
              );
            });
          })}
          
          {/* Outer donut segments (allocations) - Allocated projects */}
          {outerSegments.map((seg, idx) => (
            <path
              key={`outer-${idx}`}
              d={describeArc(50, 50, 37, 39.5, seg.startAngle, seg.endAngle)}
              fill={seg.color}
              stroke="none"
              onMouseEnter={() => setHoveredSegment({ type: 'outer', index: idx, segment: seg })}
              onMouseLeave={() => setHoveredSegment(null)}
            />
          ))}
          
          {/* FIX 2: Center circle background - transparent to show page background */}
          <circle cx="50" cy="50" r="18" fill="transparent" stroke="none" />
        </svg>

        {/* Center Controls */}
        <div 
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center justify-center gap-2"
          style={{ width: '30%', height: '30%', pointerEvents: 'none' }}
        >
          <div className="flex items-center gap-3" style={{ pointerEvents: 'auto' }}>
            {/* Project Wheel - FIX 1a: Uses allocatedProjectsForPicker (filtered + sorted) */}
            <div 
              className="relative bg-transparent rounded-lg overflow-hidden"
              style={{ 
                width: '45px',
                height: '80px',
                WebkitMaskImage: 'linear-gradient(to bottom, transparent 5%, white 25%, white 75%, transparent 95%)',
                maskImage: 'linear-gradient(to bottom, transparent 5%, white 25%, white 75%, transparent 95%)',
                pointerEvents: 'auto',
                // FIX 2: Ensure no border/shadow on picker container
                border: 'none',
                boxShadow: 'none',
                outline: 'none'
              }}
            >
              <div
                ref={projectWheelRef}
                className="absolute inset-0 overflow-y-scroll scrollbar-hide"
                style={{
                  WebkitOverflowScrolling: 'touch',
                  backgroundColor: 'transparent'
                }}
              >
                <div style={{ height: '20px' }} />
                
                {/* Show empty state when no allocations in Supabase mode (team users only) */}
                {allocatedProjectsForPicker.length === 0 && useSupabaseAllocations && !isFloatingUser && (
                  <div
                    className="flex items-center justify-center text-center px-1"
                    style={{
                      height: '40px',
                      color: '#94a3b8',
                      fontSize: '8px',
                      lineHeight: '1.2'
                    }}
                  >
                    No projects<br/>allocated
                  </div>
                )}
                
                {/* Show floating user hint when showing all projects */}
                {useSupabaseAllocations && isFloatingUser && resolvedAllocations.length === 0 && (
                  <div
                    className="flex items-center justify-center text-center px-1 mb-1"
                    style={{ 
                      height: '20px',
                      color: '#9F902A',
                      fontSize: '7px',
                      lineHeight: '1.1',
                      opacity: 0.8
                    }}
                  >
                    No team — all projects
                  </div>
                )}
                
                {/* FIX 1a: Render only allocated projects (already sorted alphabetically) */}
                {allocatedProjectsForPicker.map((project, idx) => (
                  <div
                    key={project.id}
                    className="flex items-center justify-center font-bold cursor-pointer hover:bg-transparent"
                    style={{
                      height: `${WHEEL_ITEM_HEIGHT}px`,
                      scrollSnapAlign: 'center',
                      scrollSnapStop: 'always',
                      opacity: String(selectedProject) === String(project.id) ? 1 : 0.55,
                      transform: String(selectedProject) === String(project.id) ? 'scale(1.15)' : 'scale(0.85)',
                      transition: 'opacity 280ms ease-out, transform 280ms ease-out',
                      color: '#1e293b',
                      fontSize: '10px'
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      clickWheelItem(projectWheelRef, idx, project.id, setSelectedProject, projectClickGuardRef);
                    }}
                  >
                    {/* FIX 3: Show first 4 letters instead of 3 (or 2) */}
                    {project.projectCode || project.name?.substring(0, 4).toUpperCase()}
                  </div>
                ))}
                
                <div style={{ height: '20px' }} />
              </div>
            </div>

            {/* Confirm Button */}
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleConfirmDay();
              }}
              disabled={!canConfirm}
              className="rounded-full transition-all flex items-center justify-center"
              style={{
                width: '20px',
                height: '20px',
                backgroundColor: canConfirm ? '#9F902A' : '#F5F5F5',
                border: canConfirm ? 'none' : '1px solid rgba(0,0,0,0.1)',
                cursor: canConfirm ? 'pointer' : 'not-allowed',
                opacity: canConfirm ? 1 : 0.5
              }}
              onMouseEnter={(e) => {
                if (canConfirm) e.target.style.backgroundColor = '#7F7321';
              }}
              onMouseLeave={(e) => {
                if (canConfirm) e.target.style.backgroundColor = '#9F902A';
              }}
              title="Confirm day (Enter)"
            />

            {/* Task/Stage Wheel */}
            <div 
              className="relative bg-transparent rounded-lg overflow-hidden"
              style={{ 
                width: '45px',
                height: '80px',
                WebkitMaskImage: 'linear-gradient(to bottom, transparent 5%, white 25%, white 75%, transparent 95%)',
                maskImage: 'linear-gradient(to bottom, transparent 5%, white 25%, white 75%, transparent 95%)',
                // FIX 2: Ensure no border/shadow on picker container
                border: 'none',
                boxShadow: 'none',
                outline: 'none'
              }}
            >
              <div
                ref={taskWheelRef}
                className="absolute inset-0 overflow-y-scroll scrollbar-hide"
                style={{
                  WebkitOverflowScrolling: 'touch',
                  backgroundColor: 'transparent'
                }}
              >
                <div style={{ height: '20px' }} />
                
                {/* Empty state when no stages are allocated for this project */}
                {tasksForPicker.length === 0 && (
                  <div
                    className="flex items-center justify-center text-center px-1"
                    style={{
                      height: '40px',
                      color: '#94a3b8',
                      fontSize: '7px',
                      lineHeight: '1.2',
                      userSelect: 'none',
                    }}
                  >
                    No stages<br/>allocated
                  </div>
                )}

                {tasksForPicker.map((task, idx) => (
                  <div
                    key={task.id}
                    className="flex items-center justify-center font-bold cursor-pointer hover:bg-transparent"
                    style={{
                      height: `${WHEEL_ITEM_HEIGHT}px`,
                      scrollSnapAlign: 'center',
                      scrollSnapStop: 'always',
                      opacity: String(selectedTask) === String(task.id) ? 1 : 0.55,
                      transform: String(selectedTask) === String(task.id) ? 'scale(1.15)' : 'scale(0.85)',
                      transition: 'opacity 280ms ease-out, transform 280ms ease-out',
                      color: '#1e293b',
                      fontSize: '10px'
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      clickWheelItem(taskWheelRef, idx, task.id, setSelectedTask, taskClickGuardRef);
                      if (selectedProject && task.id) {
                        // Pass explicit IDs to avoid React batch-update stale reads
                        handleAddEntry(selectedProject, task.id);
                      }
                    }}
                  >
                    {task.name.substring(0, 5)}
                  </div>
                ))}
                
                <div style={{ height: '20px' }} />
              </div>
            </div>
          </div>
        </div>

        {/* Total logged hours — hidden on site visit days */}
        {activeDate && !isSiteVisitDay && (
          <div
            className="absolute left-1/2 -translate-x-1/2 z-40"
            style={{
              bottom: (hoveredSegment || isDragging) ? '-8px' : '-20px',
              pointerEvents: 'none',
              textAlign: 'center',
              fontFamily: "'Raleway', 'Inter', sans-serif",
              transition: 'bottom 150ms ease'
            }}
          >
            <span style={{ fontSize: '12px', fontWeight: 700, color: '#1e293b', letterSpacing: '-0.02em' }}>
              {formatHours(totalLoggedHours)}
            </span>
            <span style={{ fontSize: '8px', color: '#94a3b8', fontWeight: 500, marginLeft: '3px' }}>
              logged
            </span>
          </div>
        )}
        {/* Site visit label in place of hours counter */}
        {activeDate && isSiteVisitDay && (
          <div
            className="absolute left-1/2 -translate-x-1/2 z-40"
            style={{
              bottom: '-20px',
              pointerEvents: 'none',
              textAlign: 'center',
              fontFamily: "'Raleway', 'Inter', sans-serif"
            }}
          >
            <span style={{ fontSize: '11px', fontWeight: 700, color: '#9F902A', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
              Site Visit
            </span>
          </div>
        )}

        {/* Hover/Drag Tooltip — hidden on site visit days */}
        {(hoveredSegment || isDragging) && !isSiteVisitDay && (
          <div
            className="absolute left-1/2 -translate-x-1/2 text-black text-xs whitespace-nowrap z-50"
            style={{
              bottom: '-24px',
              pointerEvents: 'none',
              backgroundColor: 'transparent'
            }}
          >
            {(() => {
              let displaySegment = null;

              if (isDragging && dragSegmentIndex !== null) {
                displaySegment = innerSegments.find(seg => seg.currentIndex === dragSegmentIndex);
              } else if (hoveredSegment) {
                if (hoveredSegment.type === 'outer') {
                  displaySegment = outerSegments[hoveredSegment.index];
                } else {
                  displaySegment = hoveredSegment.segment;
                }
              }

              if (!displaySegment) return null;

              return (
                <div className="flex items-center gap-2">
                  <span className="font-bold">{displaySegment.label}</span>
                  <span className="opacity-70">{formatHours(displaySegment.hours)}</span>
                  <div
                    className="w-2 h-2 rounded-full"
                    style={{
                      backgroundColor: displaySegment.billable ? '#9F902A' : 'transparent',
                      border: displaySegment.billable ? 'none' : '1px solid #9F902A'
                    }}
                    title={displaySegment.billable ? 'Billable' : 'Non-billable'}
                  />
                </div>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
}
